import { useEffect, useState } from 'react';
import { http } from '../../lib/api';
import { AppLayout } from '../../components/layout';
import { Button, Card, StatusBadge, Input } from '../../components/core';
import { DataTable, type Column, Modal, ConfirmDialog, SpinnerBlock } from '../../components/feedback';
import { ADMIN_NAV } from './nav';

interface AdminUserRow {
  id: string;
  email: string;
  phone: string | null;
  status: string;
  createdAt: string | null;
}

export function AdminUsersPage() {
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', phone: '', password: '', status: 'ACTIVE' });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AdminUserRow | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<AdminUserRow | null>(null);
  const [editForm, setEditForm] = useState({ email: '', phone: '', password: '', status: 'ACTIVE' });
  const [editSaving, setEditSaving] = useState(false);

  const load = () => {
    setLoading(true);
    http.get<AdminUserRow[]>('/api/admin/users')
      .then((d) => setRows(Array.isArray(d) ? d : (d as any).items ?? []))
      .catch((e) => setError(e.message ?? 'Failed'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const createUser = async () => {
    if (!form.email || !form.password) return;
    setSaving(true);
    try {
      await http.post('/api/admin/users', {
        email: form.email,
        phone: form.phone,
        password: form.password,
        status: form.status,
      });
      setShowCreate(false);
      setForm({ email: '', phone: '', password: '', status: 'ACTIVE' });
      load();
    } catch (e) { setError((e as Error).message ?? 'Create failed'); }
    setSaving(false);
  };

  const saveEdit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      const payload: Record<string, unknown> = {
        email: editForm.email,
        phone: editForm.phone,
        status: editForm.status,
      };
      if (editForm.password.trim()) payload.password = editForm.password;
      await http.patch(`/api/admin/users/${editUser.id}`, payload);
      setEditUser(null);
      load();
    } catch (e) { setError((e as Error).message ?? 'Save failed'); }
    setEditSaving(false);
  };

  const deleteUser = async () => {
    if (!confirmDelete) return;
    setActingId(confirmDelete.id);
    try {
      await http.delete(`/api/admin/users/${confirmDelete.id}`);
      setConfirmDelete(null);
      load();
    } catch (e) { setError((e as Error).message ?? 'Delete failed'); }
    setActingId(null);
  };

  const columns: Column<AdminUserRow>[] = [
    { key: 'email', header: 'Email', render: (r) => r.email },
    { key: 'phone', header: 'Phone', render: (r) => r.phone || '—' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'createdAt', header: 'Created', render: (r) => r.createdAt ? new Date(r.createdAt).toLocaleString() : '—' },
    {
      key: 'actions', header: '', render: (r) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="secondary" onClick={() => { setEditUser(r); setEditForm({ email: r.email, phone: r.phone ?? '', password: '', status: r.status }); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => setConfirmDelete(r)} disabled={actingId === r.id}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="page-head">
        <div>
          <h1>Admin Users</h1>
          <p className="text-muted">Manage platform administrators who can access this admin panel.</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>+ New Admin</Button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {loading ? <SpinnerBlock /> : (
        <Card>
          <DataTable columns={columns} rows={rows} />
          {!rows.length && <p className="text-muted text-sm" style={{ margin: 0 }}>No admin users found.</p>}
        </Card>
      )}

      {showCreate && (
        <Modal title="Create Admin User" onClose={() => setShowCreate(false)} footer={<>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button onClick={createUser} disabled={saving}>{saving ? 'Creating…' : 'Create'}</Button>
        </>}>
          <div className="modal-grid">
            <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} hint="Optional" />
            <Input label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required hint="Min 8 characters" />
            <div className="field">
              <label htmlFor="status">Status</label>
              <select id="status" className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="ACTIVE">Active</option>
                <option value="PENDING_VERIFICATION">Pending verification</option>
              </select>
            </div>
          </div>
        </Modal>
      )}

      {editUser && (
        <Modal title="Edit Admin User" onClose={() => setEditUser(null)} footer={<>
          <Button variant="secondary" onClick={() => setEditUser(null)}>Cancel</Button>
          <Button onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save'}</Button>
        </>}>
          <div className="modal-grid">
            <Input label="Email" type="email" value={editForm.email} onChange={(v) => setEditForm({ ...editForm, email: v })} required />
            <Input label="Phone" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} hint="Optional" />
            <Input label="Password" type="password" value={editForm.password} onChange={(v) => setEditForm({ ...editForm, password: v })} hint="Leave blank to keep current" />
            <div className="field">
              <label htmlFor="edit-status">Status</label>
              <select id="edit-status" className="input" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="PENDING_VERIFICATION">Pending verification</option>
              </select>
            </div>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete Admin User"
          body={`Delete "${confirmDelete.email}"? This permanently removes the admin account.`}
          confirmLabel="Delete"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => void deleteUser()}
        />
      )}
    </AppLayout>
  );
}
